"use client"

import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"
import { PIE_MODAL_ANCLADO, PIE_MODAL_ANCLADO_STYLE } from "@/lib/modalFooter"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80 lg-overlay data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

/**
 * Canonical modal width scale. Use the `size` prop instead of ad-hoc
 * `max-w-*` / `w-[*vw]` classes so every dialog in the app shares the same
 * widths and scroll behavior. Migration equivalence:
 *   sm  ← max-w-sm/md         md  ← max-w-lg (default, unchanged)
 *   lg  ← max-w-xl/2xl        xl  ← max-w-3xl/4xl
 *   2xl ← max-w-5xl/6xl       full ← w-[9x vw] h-[9x vh] (wizards/canvas)
 * `full` is meant for multi-step wizards and canvas surfaces.
 * `className` still wins over `size` (tailwind-merge), so existing callers
 * that pass their own width are unaffected.
 */
const dialogSizeVariants = {
  sm: "max-w-sm max-h-[85vh] overflow-y-auto",
  md: "max-w-lg max-h-[85vh] overflow-y-auto",
  lg: "max-w-2xl max-h-[85vh] overflow-y-auto",
  xl: "max-w-3xl max-h-[85vh] overflow-y-auto",
  "2xl": "max-w-5xl max-h-[85vh] overflow-y-auto",
  // `sm:w-[95vw]` (no `w-[95vw]` a secas) para ganarle al `sm:w-full` de la base
  // en tailwind-merge; < sm hereda el ancho con margen lateral de la base.
  full: "max-w-[95vw] sm:w-[95vw] h-[90dvh] overflow-y-auto",
}

const DialogContent = React.forwardRef(({ className, children, size, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        // Superficie `card` OPACA: el `--background` de VERIX es gris-azulado, así
        // que `bg-background/85` translúcido teñía todo modal de gris (two-tone en
        // oscuro cuando el contenido usa bandas `card`). `card` unifica la superficie.
        // El tema `liquid` (`.lg-glass` bajo `data-surface='liquid'`) sigue ganando por
        // especificidad, y cada caller puede pisar `bg-*` vía className (tailwind-merge).
        // `w-[calc(100%-1.5rem)]` deja aire a los lados en móvil (antes el modal
        // iba a sangre) y `max-h-[85dvh] overflow-y-auto` en la BASE evita que
        // los diálogos sin `size` (que no heredaban el límite de altura) se
        // salgan por arriba y abajo de la pantalla sin poder scrollear.
        "fixed left-[50%] top-[50%] z-50 grid grid-cols-1 min-w-0 w-[calc(100%-1.5rem)] sm:w-full max-w-lg max-h-[85dvh] overflow-y-auto translate-x-[-50%] translate-y-[-50%] gap-4 border bg-card p-4 sm:p-6 shadow-elev-3 duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] rounded-2xl lg-glass",
        size && dialogSizeVariants[size],
        className
      )}
      {...props}>
      {children}
      <DialogPrimitive.Close
        className="absolute right-3 top-3 inline-flex h-[40px] w-[40px] items-center justify-center rounded-full opacity-70 ring-offset-background transition-opacity hover:bg-accent hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

// `PIE_MODAL_ANCLADO`: las acciones no scrollean con el cuerpo — ver `lib/utils`.
// El `style` va ANTES del spread para que un caller pueda pisarlo si lo necesita.
const DialogFooter = ({
  className,
  style,
  ...props
}) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", PIE_MODAL_ANCLADO, className)}
    style={{ ...PIE_MODAL_ANCLADO_STYLE, ...style }}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
